/**
 * Generative-UI for the `radio` and `select` tools. sphere-mcp executes both (MDOT-25078): the
 * call answers "the question is on screen" and ENDS the turn, and the user's answer is their next
 * message. While a question is the newest thing in the conversation, the picker takes over the
 * COMPOSER (see {@link SphereAIComposerChoice}); picking sends the chosen label(s) — or the "Other"
 * free text — as an ordinary user message, and Cancel just puts the text composer back.
 *
 * In the thread the call renders as a compact recap of the question ({@link ChoiceRecap}) once it
 * is no longer pending; the answer is the user message right after it. Single vs. multiple is
 * driven by the spec's `multiple` flag: single-select submits the instant the user picks,
 * multi-select toggles a draft and submits on a Confirm button.
 */
import type { ToolCallMessagePartComponent, ToolCallMessagePartStatus } from "@assistant-ui/react";
import { Text } from "@mdotm/shadcn/legacy/components";
import {
	TextInput,
	Select,
	Checkbox,
	Radio,
	RadioGroup,
	Button,
	Form,
	SubmitButton,
} from "@mdotm/shadcn/mdotm/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { ThreadComponents } from "@mdotm/shadcn/assistant-ui";
import {
	createContext,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { RADIO_TOOL_NAME, SELECT_TOOL_NAME, parseChoiceSpec, type ChoiceOption, type ChoiceSpec } from "./choiceSpec";

// Cap the picker at the thread reading width, CENTERED in the column, so it sits in the same lane as
// the prose and the composer instead of stretching across a full-screen thread's widened column (that
// room is reserved for charts/tables); a no-op at the normal docked width.
// Radius is the composer's own (`--composer-radius`): while a question is pending this frame IS the
// composer, so the bottom of the panel mustn't change shape. The border follows the platform field
// (legacy `TextArea`): N200 at rest, N100 once the fieldset locks — the same fade a disabled input has.
const frameClassName =
	"my-2 mx-auto flex w-full max-w-(--thread-max-width) flex-col gap-2 rounded-(--composer-radius) border border-[var(--palette-N200)] p-3 disabled:border-[var(--palette-N100)]";

/**
 * Internal sentinel value for the always-appended "Other" option. It is never delivered to the
 * model: on submit any picked `OTHER_VALUE` is swapped for the user's trimmed free text (and
 * dropped if empty). Prefixed to avoid colliding with a real option's `value`.
 */
const OTHER_VALUE = "__sphere_choice_other__";
const OTHER_OPTION: ChoiceOption = { label: "Other…", value: OTHER_VALUE };

/**
 * Fixed result delivered when the user dismisses a choice via Cancel. It both unparks the agent
 * loop (any tool output resumes the turn) and instructs the model how to behave; it doubles as
 * the reload marker for rendering the dismissed state (see parseAnswer / useChoiceAnswer).
 */
export const CHOICE_CANCELLED_RESULT =
	"The user dismissed this selection without choosing an option. Do not ask the same question again " +
	"or decide on their behalf — wait for the user's next message and let it guide how you proceed.";

function ChoicePlaceholder({ children }: { children: React.ReactNode }): React.JSX.Element {
	return (
		<div className="my-2 rounded-(--composer-radius) border border-[var(--palette-N200)] p-3">
			<Text as="div" type="Body/M/Book" color={themeCSSVars.palette_N400}>
				{children}
			</Text>
		</div>
	);
}

/**
 * Confirm is the enclosing `<Form>`'s submit button: the form's `onSubmitAsync` owns delivery,
 * so pressing Enter (native implicit submission, e.g. from the "Other" box) submits too. It
 * carries no `onClick` of its own — that would double-fire alongside the form submit.
 */
function ConfirmButton({ count, disabled }: { count: number; disabled: boolean }): React.JSX.Element {
	return (
		<SubmitButton size="small" disabled={disabled} className="self-start">
			Confirm{count > 0 ? ` (${count})` : ""}
		</SubmitButton>
	);
}

/** Dismiss the question without choosing; always offered unless the picker is already locked. */
function CancelButton({ disabled, onCancel }: { disabled: boolean; onCancel: () => void }): React.JSX.Element {
	return (
		<Button palette="tertiary" size="small" disabled={disabled} onClick={onCancel} className="self-start">
			Cancel
		</Button>
	);
}

/**
 * An option's label with its optional secondary description line, shown as a Radio/Checkbox child.
 * `Body/L/Book` matches the composer input and the thread's prose (14/20) — an option is something
 * you read, so the weight stays Book; the emphasis belongs to the question above.
 */
function OptionLabel({ option }: { option: ChoiceOption }): React.JSX.Element {
	return (
		<span className="flex flex-col gap-0.5">
			<Text type="Body/L/Book" color={themeCSSVars.palette_N700}>
				{option.label}
			</Text>
			{option.description && (
				<Text type="Body/M/Book" color={themeCSSVars.palette_N400}>
					{option.description}
				</Text>
			)}
		</span>
	);
}

/**
 * Free-text box shown once the "Other" option is picked; its value is delivered in place of the
 * sentinel. Grabs focus on appearance so the user can type straight away and press Enter to submit.
 */
function OtherInput({
	value,
	disabled,
	onChange,
}: {
	value: string;
	disabled: boolean;
	onChange: (value: string) => void;
}): React.JSX.Element {
	return (
		<TextInput autoFocus value={value} disabled={disabled} placeholder="Type your answer…" onChangeText={onChange} />
	);
}

/** Parse a delivered tool result back into the selected values (single: bare string; multiple: JSON array). */
function parseAnswer(result: unknown, multiple: boolean): string[] | undefined {
	if (typeof result !== "string") return undefined;
	if (result === CHOICE_CANCELLED_RESULT) return undefined; // dismissed — not a selection
	if (!multiple) return [result];
	try {
		const parsed = JSON.parse(result);
		return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : undefined;
	} catch {
		return undefined;
	}
}

/**
 * Shared answer state for the picker. `result` is the delivered tool output once answered
 * (present on reload and after our own `addResult`); `draft` is the working selection before
 * submit. Local `submitted` / `cancelledLocal` flags lock the UI optimistically the instant the
 * user acts, before the result round-trips back through the runtime.
 *
 * `knownValues` are the offered options' values: on reload an answered value that isn't one of
 * them was a free-text "Other" answer, so we surface it as the selected "Other" option with its
 * text prefilled — keeping the locked view faithful.
 *
 * There is no seeded/edit variant: an answered question is never re-opened (see {@link ChoiceRecap}),
 * so the draft always starts empty and Cancel always means "dismiss this question".
 */
function useChoiceAnswer(
	result: unknown,
	multiple: boolean,
	knownValues: string[],
	addResult: (value: string) => void,
) {
	const cancelledFromResult = result === CHOICE_CANCELLED_RESULT;
	const answeredRaw = useMemo(
		() => (cancelledFromResult ? undefined : parseAnswer(result, multiple)),
		[cancelledFromResult, result, multiple],
	);
	// Map a delivered free-text answer back onto the "Other" option for a faithful locked view.
	const answered = useMemo(() => {
		if (!answeredRaw) return undefined;
		const known = new Set(knownValues);
		return answeredRaw.map((v) => (known.has(v) ? v : OTHER_VALUE));
	}, [answeredRaw, knownValues]);
	const answeredOtherText = useMemo(() => {
		if (!answeredRaw) return undefined;
		const known = new Set(knownValues);
		return answeredRaw.find((v) => !known.has(v));
	}, [answeredRaw, knownValues]);

	const [draft, setDraft] = useState<string[]>([]);
	const [otherDraft, setOtherDraft] = useState("");
	const [submitted, setSubmitted] = useState(false);
	const [cancelledLocal, setCancelledLocal] = useState(false);

	const cancelled = cancelledFromResult || cancelledLocal;
	const locked = submitted || answered !== undefined || cancelled;
	const selectedValues = answered ?? draft;
	const otherText = answeredOtherText ?? otherDraft;
	const otherSelected = selectedValues.includes(OTHER_VALUE);
	// Submittable once something is chosen — and, when "Other" is among the picks, once its box is filled.
	const canSubmit = selectedValues.length > 0 && (!otherSelected || otherText.trim().length > 0);

	const submit = useCallback(
		(values: string[]) => {
			if (submitted || answered !== undefined || cancelled) return;
			const other = otherDraft.trim();
			if (values.length === 0 || (values.includes(OTHER_VALUE) && other.length === 0)) return;
			// Swap the "Other" sentinel for the user's free text; the sentinel never reaches the model.
			const resolved = values.flatMap((v) => (v === OTHER_VALUE ? [other] : [v]));
			setSubmitted(true);
			addResult(multiple ? JSON.stringify(resolved) : resolved[0]);
		},
		[submitted, answered, cancelled, otherDraft, multiple, addResult],
	);

	const cancel = useCallback(() => {
		if (submitted || answered !== undefined || cancelled) return;
		// Dismiss the question by delivering the cancellation result, which unparks the agent loop.
		setCancelledLocal(true);
		addResult(CHOICE_CANCELLED_RESULT);
	}, [submitted, answered, cancelled, addResult]);

	return {
		locked,
		cancelled,
		selectedValues,
		otherSelected,
		otherText,
		setOtherText: setOtherDraft,
		setDraft,
		submit,
		cancel,
		canSubmit,
	};
}

/** Validate the streamed args; show a placeholder while incomplete, an error if it never validates. */
function useParsedSpec(args: unknown, status: ToolCallMessagePartStatus | undefined) {
	const parsed = useMemo(() => parseChoiceSpec(args), [args]);
	if (parsed.ok) return { spec: parsed.spec, fallback: null };
	const fallback =
		status?.type === "running" ? (
			<ChoicePlaceholder>Preparing options…</ChoicePlaceholder>
		) : (
			<ChoicePlaceholder>Couldn't render the options: {parsed.error}</ChoicePlaceholder>
		);
	return { spec: null, fallback };
}

/**
 * Make a freshly-shown, still-interactive picker own the keyboard: steal focus onto its
 * container on mount (so keystrokes land here, not the composer) and map Escape to Cancel.
 * Focus is stolen once, only while unanswered — a locked prompt restored on reload never grabs
 * it. Escape bubbling up from the inner controls is caught here too; the dropdown's own
 * Escape-to-close fires inside a portal and never reaches us, so it closes the menu (not the prompt).
 */
function useChoiceFocus<T extends HTMLElement>(locked: boolean, cancel: () => void) {
	const ref = useRef<T>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: mount-only — steal focus once when the picker first appears, from its initial (unlocked) state.
	useEffect(() => {
		if (!locked) ref.current?.focus({ preventScroll: true });
	}, []);
	const onKeyDown = useCallback(
		(event: React.KeyboardEvent<T>) => {
			if (event.key === "Escape") {
				event.preventDefault();
				cancel();
			}
		},
		[cancel],
	);
	return { ref, onKeyDown };
}

/** The Cancel button, or — once dismissed — a muted note in its place, paired with the Confirm button. */
function ChoiceActions({
	showConfirm,
	confirmCount,
	confirmDisabled,
	cancelled,
	locked,
	onCancel,
}: {
	showConfirm: boolean;
	confirmCount: number;
	confirmDisabled: boolean;
	cancelled: boolean;
	locked: boolean;
	onCancel: () => void;
}): React.JSX.Element {
	return (
		<div className="mt-1 flex items-center gap-2">
			{showConfirm && <ConfirmButton count={confirmCount} disabled={confirmDisabled} />}
			{cancelled ? (
				<Text type="Body/M/Book" color={themeCSSVars.palette_N400}>
					You dismissed this selection.
				</Text>
			) : (
				<CancelButton disabled={locked} onCancel={onCancel} />
			)}
		</div>
	);
}

/**
 * The interactive picker. `kind` selects the control (radio/checkbox list vs. dropdown); "Other",
 * Cancel, keyboard focus, and optimistic locking are common. A `<Form>` so the browser submits
 * natively (Enter in the "Other" box) and Confirm can be a SubmitButton.
 *
 * One use only: the composer shows it for a PENDING question. A settled answer is history and is
 * never re-opened here — it renders read-only as a {@link ChoiceRecap}.
 */
function ChoiceForm({
	kind,
	spec,
	result,
	addResult,
}: {
	kind: typeof RADIO_TOOL_NAME | typeof SELECT_TOOL_NAME;
	spec: ChoiceSpec;
	result: unknown;
	addResult: (value: string) => void;
}): React.JSX.Element {
	const { multiple } = spec;
	const knownValues = useMemo(() => spec.options.map((o) => o.value), [spec]);
	const {
		locked,
		cancelled,
		selectedValues,
		otherSelected,
		otherText,
		setOtherText,
		setDraft,
		submit,
		cancel,
		canSubmit,
	} = useChoiceAnswer(result, multiple, knownValues, addResult);
	const { ref, onKeyDown } = useChoiceFocus<HTMLFieldSetElement>(locked, cancel);

	// "Other" is always offered as the last option (free text via the box revealed when picked).
	const options = useMemo(() => [...spec.options, OTHER_OPTION], [spec]);
	const toggle = (value: string) =>
		setDraft((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
	// Confirm appears for multi-select, and for single-select once "Other" is picked (its free text
	// needs a submit step; every other single pick submits instantly).
	const showConfirm = multiple || otherSelected;

	return (
		<Form onSubmitAsync={async () => submit(selectedValues)} className="contents">
			<fieldset
				ref={ref}
				tabIndex={-1}
				onKeyDown={onKeyDown}
				className={`${frameClassName} outline-none`}
				disabled={locked}
			>
				{/* `float-left w-full` is what stops the question being notched INTO the frame's top border.
				    A `<legend>` is by default the fieldset's "rendered legend", which the browser lifts out
				    of the content box and draws over the border — a look that appears nowhere else in the
				    product (this is the app's only fieldset). Per the HTML spec a floated legend is no
				    longer the rendered legend, so it falls back into normal flow as the frame's first
				    child, which is where every other radio group in the platform puts its label (see
				    e.g. CreatePortfolio's spawn-asset-allocation). Presentation only: it stays a `<legend>`,
				    so it still names the fieldset for assistive tech, and the float itself is inert once
				    it is an ordinary flex item of the frame — `w-full` is what gives it its own line.

				    Size stays Body/L (14px) rather than the platform label's Body/M (12px) on purpose:
				    the thread deliberately runs one step above the platform's running text, and the
				    question must not read smaller than the options under it. */}
				<Text as="legend" type="Body/L/Medium" color={themeCSSVars.palette_N700} className="float-left w-full">
					{spec.question}
				</Text>
				{kind === RADIO_TOOL_NAME ? (
					multiple ? (
						// Checkboxes manage their own toggling; `RadioGroup` (below) owns single-select state and naming.
						<div className="flex flex-col gap-2">
							{options.map((option) => (
								<Checkbox
									key={option.value}
									checked={selectedValues.includes(option.value)}
									disabled={locked}
									onChange={() => toggle(option.value)}
								>
									<OptionLabel option={option} />
								</Checkbox>
							))}
						</div>
					) : (
						<RadioGroup
							value={selectedValues[0] ?? null}
							disabled={locked}
							onChange={(value) => {
								setDraft([value]);
								// Instant submit for a concrete pick; "Other" waits for its free text + Confirm.
								if (value !== OTHER_VALUE) submit([value]);
							}}
						>
							<div className="flex flex-col gap-2">
								{options.map((option) => (
									<Radio key={option.value} value={option.value}>
										<OptionLabel option={option} />
									</Radio>
								))}
							</div>
						</RadioGroup>
					)
				) : multiple ? (
					<Select
						multi
						options={options}
						value={selectedValues}
						onChange={(values) => setDraft(values)}
						disabled={locked}
						placeholder="Select options…"
						enableSearch={options.length > 8}
					/>
				) : (
					<Select
						options={options}
						value={selectedValues[0] ?? null}
						onChange={(value) => {
							if (value != null) {
								setDraft([value]);
								// Instant submit for a concrete pick; "Other" waits for its free text + Confirm.
								if (value !== OTHER_VALUE) submit([value]);
							}
						}}
						disabled={locked}
						placeholder="Select an option…"
						enableSearch={options.length > 8}
					/>
				)}
				{otherSelected && <OtherInput value={otherText} disabled={locked} onChange={setOtherText} />}
				<ChoiceActions
					showConfirm={showConfirm}
					confirmCount={multiple ? selectedValues.length : 0}
					confirmDisabled={locked || !canSubmit}
					cancelled={cancelled}
					locked={locked}
					onCancel={cancel}
				/>
			</fieldset>
		</Form>
	);
}

/**
 * Compact frame for the settled recap — a quiet card, deliberately unlike the interactive picker:
 * the N100 hairline (the panel's own divider weight) instead of the field's N200 border.
 */
const recapClassName =
	"my-2 flex w-full flex-col gap-1 rounded-(--composer-radius) border border-[var(--palette-N100)] p-3";

/**
 * The settled, in-thread record of an asked choice: just the question — not the picker. The answer
 * is the user message that follows it.
 *
 * Read-only by design: the conversation is append-only, so a delivered answer is never re-opened
 * for editing. Changing it would mean regenerating everything that followed, which cannot undo the
 * actions that answer already set in motion — the user picks again by saying so in a new message.
 */
function ChoiceRecap({ spec }: { spec: ChoiceSpec }): React.JSX.Element {
	return (
		<div className={recapClassName}>
			<Text type="Body/M/Book" color={themeCSSVars.palette_N400}>
				{spec.question}
			</Text>
		</div>
	);
}

/**
 * The error text assistant-ui packs into `result` (as `{ error }`) for a tool call that failed
 * instead of returning an output — for a choice call, the `errorText` delivered by `onToolCall`.
 */
function errorResultText(result: unknown): string | undefined {
	if (typeof result !== "object" || result === null || !("error" in result)) return undefined;
	const { error } = result as { error: unknown };
	return typeof error === "string" && error.length > 0 ? error : undefined;
}

/**
 * A choice call that failed once its question was known: the spec validated, so the question is
 * shown, and the failure's own text stands where the answer would. That text is the only account of
 * what went wrong — the AI SDK clears a failed call's `input`, so `args` describes nothing.
 */
function ChoiceFailure({ question, error }: { question: string; error: string | undefined }): React.JSX.Element {
	return (
		<div className={recapClassName}>
			<Text type="Body/M/Book" color={themeCSSVars.palette_N400}>
				{question}
			</Text>
			<Text type="Body/M/Italic" color={themeCSSVars.palette_N400}>
				{error ?? "This question couldn't be put to you."}
			</Text>
		</div>
	);
}

/**
 * Inline (in-thread) renderer for a choice tool call. While the call is still streaming
 * (`result` undefined) or is the pending prompt, the interactive picker lives in the composer (see
 * {@link SphereAIComposerChoice}), so inline we render nothing; otherwise we show a
 * {@link ChoiceRecap} of the question, followed in the thread by the user's answer message.
 *
 * A failed call has no answer to record. With a valid spec its question was real, so the failure is
 * shown under it ({@link ChoiceFailure}); without one the call was rejected before it could be put
 * to the user, and the thread stays clean — the model already has the error and asks again.
 *
 * The recap is final: there is no affordance to re-answer a settled question. The transcript is
 * append-only, and an answer the model has already acted on can't be walked back by rewriting it.
 */
const InlineChoice: ToolCallMessagePartComponent = ({ args, status, result, isError, toolCallId }) => {
	const { spec, fallback } = useParsedSpec(args, status);
	const pending = useContext(SphereAIChoicePromptContext);
	// Still the open question → the composer owns the picker.
	if (result === undefined || pending?.toolCallId === toolCallId) return null;
	if (isError) return spec ? <ChoiceFailure question={spec.question} error={errorResultText(result)} /> : null;
	if (!spec) return fallback;
	return <ChoiceRecap spec={spec} />;
};

/**
 * `Tools` registry entries for the choice renderers, spread into the Thread's by-name
 * registry alongside the chart renderer. A stable module constant so the Thread can
 * memoize its part grouping across renders. Both tools share the one read-only recap renderer.
 */
export const sphereAIChoiceTools = {
	[RADIO_TOOL_NAME]: InlineChoice,
	[SELECT_TOOL_NAME]: InlineChoice,
} satisfies NonNullable<ThreadComponents["Tools"]>;

/**
 * The pending choice question the composer should render in place of the text input, or
 * `null` when there's none. Provided by SphereAIChatSection (which holds the live chat and so
 * can spot the parked `radio`/`select` tool call and deliver its answer) and consumed by
 * {@link SphereAIComposerChoice} deep inside the styled Thread's composer — a React context
 * is the bridge because the Thread's composer slots receive no props (cf. AppContextChip).
 */
export type SphereAIChoicePrompt = {
	/** Identity of the parked tool call; keys the composer picker so a new question always mounts fresh. */
	toolCallId: string;
	toolName: typeof RADIO_TOOL_NAME | typeof SELECT_TOOL_NAME;
	/** The raw tool-call input; validated here into a ChoiceSpec. */
	args: unknown;
	/** Deliver the answer (option `value`, JSON array of values, or CHOICE_CANCELLED_RESULT). */
	addResult: (value: string) => void;
};

const SphereAIChoicePromptContext = createContext<SphereAIChoicePrompt | null>(null);

export const SphereAIChoicePromptProvider = SphereAIChoicePromptContext.Provider;

/**
 * The Thread's `Composer` override: while a `radio`/`select` question is pending it replaces
 * the text composer with the matching picker; otherwise it renders the default composer
 * (`children`) untouched. Delivering the answer clears the pending prompt upstream, so the
 * composer reverts to plain text on its own.
 */
export function SphereAIComposerChoice({ children }: PropsWithChildren): React.JSX.Element {
	const prompt = useContext(SphereAIChoicePromptContext);
	// A defensive parse: invalid specs are acked as errors in onToolCall and never park, so a
	// pending prompt always has a valid spec — but fall back to the composer if that ever fails.
	const parsed = prompt ? parseChoiceSpec(prompt.args) : null;
	if (!prompt || !parsed?.ok) return <>{children}</>;
	// Key on the tool-call id so consecutive questions never share a ChoiceForm instance: without
	// this, when the answered → next-question transition batches into a single render (no intervening
	// null prompt to unmount), the picker inherits the prior question's `submitted`/`cancelledLocal`
	// lock and the whole fieldset — Cancel included — stays disabled.
	return (
		<ChoiceForm
			key={prompt.toolCallId}
			kind={prompt.toolName}
			spec={parsed.spec}
			result={undefined}
			addResult={prompt.addResult}
		/>
	);
}
